import { Button, EmptyState, FieldBlock, Input, LoadingState, useToast } from '@orbind/ui'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { ExplorerBody, useExplorer, useExplorerConfig } from '../shell/explorer.tsx'
import { MOCK_ME, PRESENCE_LABEL, type ChatChannel, type ChatMessage } from '../mocks/chat.ts'
import { MOCK_CONTACTS, MOCK_GROUPS, MOCK_GROUP_MESSAGES, type GroupContact } from '../mocks/groups.ts'
import { ChatConversation, ChatRow, ExplorerDrawer } from './chat-shared.tsx'
import './channels.css'

/**
 * Grupos — conversas informais montadas a partir de pessoas.
 *
 * Mesma anatomia da Comunicacao (lista no explorer do shell + conversa no
 * `<main>`), reusando `ChatRow`/`ChatConversation`/`ExplorerDrawer` de
 * `chat-shared.tsx`. O que muda e a criacao: o protótipo faz "Novo grupo" em
 * DOIS passos dentro da gaveta (`docs/design-slices/_shell.html:88-158`) —
 * primeiro os participantes, depois foto e nome.
 *
 * Dados mocados: criar grupo e enviar mensagem vivem so no estado local.
 */
export function GroupsPage() {
  const navigate = useNavigate()
  const { id: selectedId = null } = useParams()
  const toast = useToast()
  const { search, chip } = useExplorer()

  const [loading, setLoading] = useState(true)
  const [criados, setCriados] = useState<ChatChannel[]>([])
  const [mensagensLocais, setMensagensLocais] = useState<Record<string, ChatMessage[]>>({})
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [lidos, setLidos] = useState<readonly string[]>([])
  const [separadores, setSeparadores] = useState<Record<string, string>>({})

  // Fluxo de criacao: `null` = fechado; 1 = participantes; 2 = foto e nome.
  const [passo, setPasso] = useState<1 | 2 | null>(null)
  const [selecionados, setSelecionados] = useState<readonly GroupContact[]>([])
  const [buscaContato, setBuscaContato] = useState('')
  const [nomeGrupo, setNomeGrupo] = useState('')

  const groups = useMemo(() => [...criados, ...MOCK_GROUPS], [criados])
  const selected = groups.find((g) => g.id === selectedId) ?? null

  const messages = useMemo(() => {
    if (selected === null) return []
    return [...(MOCK_GROUP_MESSAGES[selected.id] ?? []), ...(mensagensLocais[selected.id] ?? [])]
  }, [selected, mensagensLocais])

  const naoLidasDe = (group: ChatChannel) => (lidos.includes(group.id) ? 0 : group.unread)

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 320)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (selectedId === null || lidos.includes(selectedId)) return
    const primeira = (MOCK_GROUP_MESSAGES[selectedId] ?? []).find((m) => m.kind === 'message' && !m.mine && !m.read)
    if (primeira) setSeparadores((atual) => ({ ...atual, [selectedId]: primeira.id }))
    setLidos((atual) => [...atual, selectedId])
  }, [selectedId, lidos])

  // chips do modulo `groups` em `shell/modules.ts`: Todos / Não lidas / Menções
  const rows = groups.filter((group) => {
    if (chip === 'Não lidas' && naoLidasDe(group) === 0) return false
    if (chip === 'Menções' && !group.mentionsMe) return false
    const busca = search.trim().toLowerCase()
    if (busca === '') return true
    return (
      group.name.toLowerCase().includes(busca) ||
      group.lastMessagePreview.toLowerCase().includes(busca) ||
      group.members.some((membro) => membro.toLowerCase().includes(busca))
    )
  })

  const abrirCriacao = () => {
    setSelecionados([])
    setBuscaContato('')
    setNomeGrupo('')
    setPasso(1)
  }

  useExplorerConfig({
    footer: loading
      ? 'Carregando grupos…'
      : `${rows.length} ${rows.length === 1 ? 'grupo' : 'grupos'} · ${groups.reduce(
          (total, group) => total + naoLidasDe(group),
          0,
        )} não lidas`,
    onCreate: abrirCriacao,
  })

  const draft = selected ? (drafts[selected.id] ?? '') : ''

  const enviar = () => {
    if (selected === null) return
    const texto = draft.trim()
    if (texto === '') return
    const agora = new Date()
    const message: ChatMessage = {
      id: `local-${selected.id}-${agora.getTime()}`,
      kind: 'message',
      author: MOCK_ME.name,
      initials: MOCK_ME.initials,
      text: texto,
      time: agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
      mine: true,
      read: true,
    }
    const grupoId = selected.id
    setMensagensLocais((atual) => ({ ...atual, [grupoId]: [...(atual[grupoId] ?? []), message] }))
    setDrafts((atual) => ({ ...atual, [grupoId]: '' }))
  }

  const alternarContato = (contato: GroupContact) => {
    setSelecionados((atual) =>
      atual.some((c) => c.id === contato.id) ? atual.filter((c) => c.id !== contato.id) : [...atual, contato],
    )
  }

  const contatosVisiveis = MOCK_CONTACTS.filter((contato) => {
    const busca = buscaContato.trim().toLowerCase()
    if (busca === '') return true
    return contato.name.toLowerCase().includes(busca) || contato.area.toLowerCase().includes(busca)
  })

  const criarGrupo = () => {
    if (selecionados.length === 0) return
    // Protótipo `_shell.html:1791`: sem nome, o grupo herda os primeiros nomes.
    const nome = nomeGrupo.trim() === '' ? selecionados.map((c) => c.name.split(' ')[0]).join(', ') : nomeGrupo.trim()
    const participantes = selecionados.map((c) => c.name)
    const id = `gr-local-${Date.now()}`
    const group: ChatChannel = {
      id,
      name: nome,
      kind: 'time',
      description: 'Grupo criado nesta sessão.',
      icon: 'fas fa-users',
      members: [...participantes, MOCK_ME.name],
      memberCount: participantes.length + 1,
      unread: 0,
      mentionsMe: false,
      lastMessagePreview: `Você criou o grupo “${nome}”`,
      lastMessageAt: 'agora',
      presence: null,
      liveMeetingId: null,
      contextCode: null,
      contextLabel: null,
      readOnly: false,
    }
    setCriados((atual) => [group, ...atual])
    setMensagensLocais((atual) => ({
      ...atual,
      [id]: [
        { id: `${id}-d`, kind: 'date', text: 'Hoje' },
        {
          id: `${id}-s`,
          kind: 'system',
          text: `Você criou o grupo “${nome}” com ${participantes.length} participante${
            participantes.length > 1 ? 's' : ''
          }`,
          contextCode: null,
        },
      ],
    }))
    setPasso(null)
    navigate(`/groups/${id}`)
    toast.push({ tone: 'success', message: `Grupo “${nome}” criado. Todos os participantes foram notificados.` })
  }

  const chipsSelecionados = (removivel: boolean) => (
    <div className="ch-groupchips">
      {selecionados.map((contato) =>
        removivel ? (
          <button
            key={contato.id}
            type="button"
            className="ch-groupchip"
            title={`Remover ${contato.name}`}
            aria-label={`Remover ${contato.name}`}
            onClick={() => alternarContato(contato)}
          >
            <span className="ch-groupchip__ini">{contato.initials}</span>
            {contato.name}
            <i className="fas fa-times" aria-hidden style={{ fontSize: 10 }} />
          </button>
        ) : (
          <span key={contato.id} className="ch-groupchip ch-groupchip--static">
            <span className="ch-groupchip__ini">{contato.initials}</span>
            {contato.name}
          </span>
        ),
      )}
    </div>
  )

  return (
    <>
      <ExplorerBody>
        {loading ? (
          <div style={{ padding: 14 }}>
            <LoadingState rows={4} label="Carregando grupos" />
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: '28px 16px' }}>
            <EmptyState
              icon="fas fa-users"
              title="Nenhum grupo encontrado"
              description="Nenhum grupo combina com a busca e o filtro selecionados. Crie um grupo escolhendo os participantes."
              action={
                <Button variant="secondary" icon="fas fa-plus" onClick={abrirCriacao}>
                  Novo grupo
                </Button>
              }
            />
          </div>
        ) : (
          rows.map((group) => (
            <ChatRow
              key={group.id}
              channel={group}
              selected={group.id === selectedId}
              unread={naoLidasDe(group)}
              onClick={() => navigate(`/groups/${group.id}`)}
            />
          ))
        )}
      </ExplorerBody>

      {passo === 1 ? (
        <ExplorerDrawer
          title="Novo grupo"
          subtitle={
            selecionados.length === 0
              ? 'Passo 1 de 2 · escolha os participantes'
              : `Passo 1 de 2 · ${selecionados.length} selecionado${selecionados.length > 1 ? 's' : ''}`
          }
          onClose={() => setPasso(null)}
          footer={
            <Button
              icon="fas fa-arrow-right"
              disabled={selecionados.length === 0}
              onClick={() => setPasso(2)}
              title="Avançar"
            >
              Avançar
            </Button>
          }
        >
          <div className="ch-grouppick">
            {selecionados.length > 0 ? chipsSelecionados(true) : null}
            <div className="ch-groupsearch">
              <i className="fas fa-search" aria-hidden />
              <input
                type="search"
                value={buscaContato}
                placeholder="Pesquisar nome ou área"
                aria-label="Pesquisar nome ou área"
                onChange={(e) => setBuscaContato(e.target.value)}
              />
            </div>
          </div>

          <div className="ch-contacts">
            {contatosVisiveis.length === 0 ? (
              <div style={{ padding: '24px 10px' }}>
                <EmptyState
                  icon="fas fa-user-slash"
                  title="Ninguém encontrado"
                  description="Nenhuma pessoa combina com essa busca."
                  action={
                    <Button variant="secondary" icon="fas fa-rotate-left" onClick={() => setBuscaContato('')}>
                      Limpar busca
                    </Button>
                  }
                />
              </div>
            ) : (
              contatosVisiveis.map((contato) => {
                const marcado = selecionados.some((c) => c.id === contato.id)
                return (
                  <button
                    key={contato.id}
                    type="button"
                    className="ch-contact"
                    aria-pressed={marcado}
                    onClick={() => alternarContato(contato)}
                  >
                    <span className="ch-avatarwrap">
                      <span className="ch-contact__avatar">{contato.initials}</span>
                      <span
                        className={`ch-dot ch-dot--${contato.presence}`}
                        title={PRESENCE_LABEL[contato.presence]}
                        aria-label={PRESENCE_LABEL[contato.presence]}
                        role="img"
                      />
                    </span>
                    <span className="ch-contact__main">
                      <span className="ch-contact__name">{contato.name}</span>
                      <span className="ch-contact__sub">{contato.note}</span>
                    </span>
                    <span className={`ch-contact__check${marcado ? ' ch-contact__check--on' : ''}`}>
                      <i className="fas fa-check" aria-hidden />
                      <span className="ob-sr-only">{marcado ? 'Selecionado' : 'Não selecionado'}</span>
                    </span>
                  </button>
                )
              })
            )}
          </div>
        </ExplorerDrawer>
      ) : null}

      {passo === 2 ? (
        <ExplorerDrawer
          title="Novo grupo"
          subtitle="Passo 2 de 2 · foto e nome"
          onClose={() => setPasso(1)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setPasso(1)}>
                Voltar
              </Button>
              <Button icon="fas fa-check" onClick={criarGrupo}>
                Criar grupo
              </Button>
            </>
          }
        >
          <div className="ch-drawer__form">
            <button
              type="button"
              className="ch-groupphoto"
              title="Adicionar foto do grupo"
              onClick={() =>
                toast.push({ tone: 'info', message: 'Enviar foto do grupo ainda não está disponível nesta prévia.' })
              }
            >
              <i className="fas fa-camera" aria-hidden style={{ fontSize: 20 }} />
              <span>Adicionar foto</span>
            </button>

            <FieldBlock
              label="Nome do grupo"
              hint="Sem nome, o grupo usa os primeiros nomes dos participantes."
            >
              {({ id }) => (
                <Input
                  id={id}
                  value={nomeGrupo}
                  placeholder="Ex.: Squad Validação de CNPJ"
                  onChange={(e) => setNomeGrupo(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') criarGrupo()
                  }}
                />
              )}
            </FieldBlock>

            <FieldBlock label={`Participantes · ${selecionados.length}`}>{() => chipsSelecionados(false)}</FieldBlock>
          </div>
        </ExplorerDrawer>
      ) : null}

      {selected === null ? (
        <div className="ch-blank">
          <EmptyState
            icon="fas fa-users"
            title="Selecione um grupo"
            description="Escolha um grupo no painel para ver as mensagens, ou crie um grupo escolhendo os participantes."
            action={
              <Button icon="fas fa-plus" onClick={abrirCriacao}>
                Novo grupo
              </Button>
            }
          />
        </div>
      ) : (
        <ChatConversation
          channel={selected}
          messages={messages}
          dividerId={separadores[selected.id] ?? null}
          draft={draft}
          onDraftChange={(texto) => setDrafts((atual) => ({ ...atual, [selected.id]: texto }))}
          onSend={enviar}
          onOpenContext={(codigo) => navigate(codigo.startsWith('DMD') ? '/demands/inbox' : '/work-items')}
          onOpenMeeting={() => navigate('/meetings')}
          onCreateWorkItem={() => navigate('/work-items')}
        />
      )}
    </>
  )
}
